import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDocument } from "../../contexts/DocumentContext";
import { Heading, Subheading } from "../../design/heading";
import LoadingView from "../../components/LoadingView";
import { useMatter } from "../../contexts/MatterContext";
import DeleteDocumentButton from "./components/DeleteDocumentButton";
import { useSeenState } from "../../contexts/SeenStateContext";
import LegalEditor from "./components/LegalEditor";
import moment from "moment";
import PublishDocumentButton from "./components/PublishDocumentButton";
import { Descendant } from "slate";
import { useUser } from "../../contexts/UserContext";
import EditDocumentButton from "./components/EditDocumentButton";
import Breadcrumbs from "../../components/Breadcrumbs";
import { useTeamMember } from "../../contexts/TeamMemberContext";
import { Text } from "../../design/text";
import ErrorView from "../../components/ErrorView";

const DocumentDetailPage: React.FC = () => {
  const { currentUser } = useUser();
  const { teamMembers } = useTeamMember();
  const { recordSeenState } = useSeenState();
  const { documentId } = useParams<{ documentId: string }>();
  const { document, setSelectedDocument, loading } = useDocument();
  const [content, setContent] = useState<Descendant[] | null>(null);

  useEffect(() => {
    if (!documentId || !currentUser) return;
    setSelectedDocument(documentId);
    recordSeenState({ documentId });
  }, [documentId, currentUser]);

  if (loading.details) return <LoadingView title="Loading Document" />;
  if (!document) return <ErrorView error={new Error("Document not found")} />;

  const handleChange = (newContent: Descendant[]) => {
    setContent(newContent);
  };

  const updatedBy = (teamMembers ?? [])
    .map((a) => a.user)
    .find((b) => b?._id === document.updatedById);

  return (
    <div className="">
      <Breadcrumbs
        items={[
          { label: "Documents", href: "/documents" },
          { label: document.name, href: `/document/${document._id}` },
        ]}
      />
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex flex-col w-full gap-6">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-2">
                <Heading>{document.name}</Heading>
                {document.description && <Text>{document.description}</Text>}
                <Subheading>
                  Updated by {updatedBy?.firstName ?? document.updatedById} @{" "}
                  {moment(document.updatedAt).format("lll")}
                </Subheading>
              </div>
              <div className="flex gap-2">
                <EditDocumentButton document={document} />
                <DeleteDocumentButton documentId={document._id} />
                <PublishDocumentButton
                  documentId={document._id}
                  content={JSON.stringify(content)}
                />
              </div>
            </div>
          </div>
          <div>
            <div className="flex">
              <div className="flex-grow">
                <LegalEditor
                  documentId={documentId!}
                  onChange={handleChange}
                  initialValue={
                    document.content ? JSON.parse(document.content) : undefined
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentDetailPage;
